import React, { useState } from 'react';
import { UserProfile, Gender } from '../types';
import { Button } from './Button';
import { auth } from '../services/firebase';

interface ProfileStepProps {
  user: UserProfile;
  onUpdate: (updatedProfile: UserProfile) => Promise<void>;
  onLogout: () => void;
  onUpgrade: () => void;
  onBack: () => void;
}

const AVATAR_OPTIONS = [
  // Male options
  'https://api.dicebear.com/9.x/micah/svg?seed=Felix&backgroundColor=b6e3f4',
  'https://api.dicebear.com/9.x/micah/svg?seed=Joshua&backgroundColor=c0aede',
  'https://api.dicebear.com/9.x/micah/svg?seed=Nolan&backgroundColor=ffdfbf',
  // Female options
  'https://api.dicebear.com/9.x/micah/svg?seed=Jessica&backgroundColor=fdf4cd',
  'https://api.dicebear.com/9.x/micah/svg?seed=Lily&backgroundColor=ffdfbf',
  'https://api.dicebear.com/9.x/micah/svg?seed=Amelia&backgroundColor=b6e3f4',
];

export const ProfileStep: React.FC<ProfileStepProps> = ({ user, onUpdate, onLogout, onUpgrade, onBack }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user.name);
  const [gender, setGender] = useState<Gender>(user.gender);
  const [targetGender, setTargetGender] = useState<Gender>(user.targetGender);
  const [selectedAvatar, setSelectedAvatar] = useState<string | undefined>(user.photoURL);

  const handleSave = async () => {
    await onUpdate({
      ...user,
      name,
      gender,
      targetGender,
      photoURL: selectedAvatar
    });
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setName(user.name);
    setGender(user.gender);
    setTargetGender(user.targetGender);
    setSelectedAvatar(user.photoURL);
    setIsEditing(true);
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50 pb-safe">
      {/* Cover / Header Section */}
      <div className="bg-gradient-to-br from-brand-600 to-purple-700 pt-8 pb-16 px-6 text-white text-center rounded-b-[2rem] shadow-lg relative">
        <button 
          onClick={onBack}
          className="absolute top-4 left-4 p-2 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-2xl font-bold">My Profile</h1>
        <p className="text-brand-100 text-sm">Manage your settings and subscription</p>
      </div>

      <div className="px-6 -mt-12 mb-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 flex flex-col items-center">
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-full border-4 border-white shadow-md overflow-hidden bg-slate-100 flex items-center justify-center relative">
               {isEditing && selectedAvatar ? (
                 <img src={selectedAvatar} alt="Selected" className="w-full h-full object-cover" />
               ) : user.photoURL ? (
                 <img src={user.photoURL} alt={user.name} className="w-full h-full object-cover" />
               ) : (
                 <span className="text-3xl font-bold text-slate-300">{user.name.charAt(0).toUpperCase()}</span>
               )}
            </div>
            {/* Camera icon removed as requested */}
          </div>

          {!isEditing ? (
             <>
                <h2 className="text-xl font-bold text-slate-900">{user.name}</h2>
                <p className="text-slate-500 text-sm mb-4">{auth.currentUser?.email}</p>
                <button 
                    onClick={handleEditClick}
                    className="text-brand-600 font-medium text-sm hover:underline"
                >
                    Edit Profile Details
                </button>
             </>
          ) : (
             <div className="w-full space-y-5">
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Choose an Avatar</label>
                    <div className="grid grid-cols-6 gap-2 mt-2">
                        {AVATAR_OPTIONS.map((avatar, idx) => (
                            <button
                                key={idx}
                                onClick={() => setSelectedAvatar(avatar)}
                                className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${
                                    selectedAvatar === avatar 
                                        ? 'border-brand-500 ring-2 ring-brand-200 scale-110' 
                                        : 'border-slate-200 opacity-70 hover:opacity-100'
                                }`}
                            >
                                <img src={avatar} alt={`Avatar ${idx}`} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Name</label>
                    <input 
                        type="text" 
                        value={name} 
                        onChange={(e) => setName(e.target.value)}
                        className="w-full border-b border-slate-300 py-2 focus:border-brand-500 outline-none font-medium"
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">My Gender</label>
                    <div className="flex gap-2 mt-1">
                        {[Gender.Male, Gender.Female, Gender.NonBinary].map(g => (
                            <button
                                key={g}
                                onClick={() => setGender(g)}
                                className={`px-3 py-1 rounded-full text-xs border capitalize ${gender === g ? 'bg-brand-50 border-brand-500 text-brand-700' : 'border-slate-200 text-slate-600'}`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Target Gender</label>
                    <div className="flex gap-2 mt-1">
                        {[Gender.Male, Gender.Female, Gender.NonBinary].map(g => (
                            <button
                                key={g}
                                onClick={() => setTargetGender(g)}
                                className={`px-3 py-1 rounded-full text-xs border capitalize ${targetGender === g ? 'bg-brand-50 border-brand-500 text-brand-700' : 'border-slate-200 text-slate-600'}`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex gap-2 pt-2">
                    <Button onClick={handleSave} className="flex-1 py-2 text-sm">Save</Button>
                    <Button variant="ghost" onClick={() => setIsEditing(false)} className="flex-1 py-2 text-sm">Cancel</Button>
                </div>
             </div>
          )}
        </div>
      </div>

      <div className="px-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide mb-4">Subscription Plan</h3>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-1 ${
                         user.planType === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    }`}>
                        {user.planType === 'trial' ? 'Free Trial' : user.planType === 'monthly' ? 'Monthly Pro' : 'Yearly Pro'}
                    </div>
                    <div className="flex flex-col">
                        <p className="text-slate-500 text-sm">
                            Expires: <span className="font-medium text-slate-800">{formatDate(user.subscriptionExpiryDate)}</span>
                        </p>
                    </div>
                </div>
                <button 
                    onClick={onUpgrade}
                    className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
                >
                    Upgrade
                </button>
            </div>
            {user.planType === 'trial' && (
                <div className="bg-blue-50 text-blue-700 text-xs p-3 rounded-lg">
                    Upgrade to Pro to unlock unlimited chats and advanced scenarios.
                </div>
            )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-1">
            <button 
                onClick={onLogout}
                className="w-full py-4 text-red-600 font-medium hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
            </button>
        </div>
        
        <div className="text-center text-xs text-slate-400 pb-4">
            Version 1.0.4
        </div>
      </div>
    </div>
  );
};